export const PROPOSAL_SERVICE_LINE_KINDS = [
  'transport',
  'customs_broker',
  'certification',
  'payment_agent',
] as const

export type ProposalServiceLineKind = typeof PROPOSAL_SERVICE_LINE_KINDS[number]

export function proposalServiceLineTitleKey(kind: string): string {
  return `applications__text__proposalServiceLine_${kind}`
}

export function parseProposalMoneyInput(value: string): string {
  const normalized = value.replace(/\s/g, '').replace(',', '.')
  if (!normalized) return ''
  const numeric = Number(normalized)
  if (!Number.isFinite(numeric)) return value.trim()
  return numeric.toFixed(2)
}

export function proposalAmountsEqual(a: string, b: string): boolean {
  const left = parseProposalMoneyInput(a)
  const right = parseProposalMoneyInput(b)
  if (!left || !right) return left === right
  return Number(left) === Number(right)
}

/** Изменение суммы приходит с бэкенда строкой со знаком («-246.80»); здесь только форматирование. */
export function formatAmountDelta(
  delta: string | null | undefined,
  currencyCode: string,
): { text: string; direction: 'up' | 'down' } | null {
  const raw = delta?.trim()
  if (!raw) {
    return null
  }
  const amount = Number(raw)
  const hasVisibleChange = Number.isFinite(amount) && Math.abs(amount) >= 0.005
  if (!hasVisibleChange) {
    return null
  }
  const isDown = raw.startsWith('-')
  const magnitude = isDown ? raw.slice(1) : raw.replace(/^\+/, '')
  const formatted = new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(magnitude))
  return {
    text: `${isDown ? '−' : '+'}${formatted} ${currencyCode}`,
    direction: isDown ? 'down' : 'up',
  }
}

function resolveProposalLineAmount(
  originalAmount: string,
  amount: string | null | undefined,
): string {
  const original = parseProposalMoneyInput(originalAmount) || '0'
  if (!amount?.trim()) {
    return original
  }
  const parsed = parseProposalMoneyInput(amount)
  if (!parsed) {
    return original
  }
  if (Number(parsed) === 0 && Number(original) !== 0) {
    return original
  }
  return parsed
}

export function buildDefaultProposalServiceLines(
  proposal: ApplicationProposal | null | undefined,
  baselines?: ApplicationProposalServiceLineBaseline[] | null,
): ApplicationProposalServiceLineForm[] {
  const fromApi = proposal?.service_lines
  if (fromApi?.length) {
    return PROPOSAL_SERVICE_LINE_KINDS.map((kind) => {
      const row = fromApi.find((line) => line.kind === kind)
      const originalAmount = row?.original_amount ?? '0'
      return {
        kind,
        original_amount: originalAmount,
        amount: resolveProposalLineAmount(originalAmount, row?.amount),
        comment: row?.comment ?? '',
      }
    })
  }
  if (baselines?.length && !proposal) {
    return PROPOSAL_SERVICE_LINE_KINDS.map((kind) => {
      const row = baselines.find((line) => line.kind === kind)
      const originalAmount = row?.original_amount ?? '0'
      return {
        kind,
        original_amount: originalAmount,
        amount: resolveProposalLineAmount(originalAmount, originalAmount),
        comment: '',
      }
    })
  }
  return PROPOSAL_SERVICE_LINE_KINDS.map((kind) => ({
    kind,
    original_amount: '0',
    amount: '0',
    comment: '',
  }))
}

export function buildProposalDraftBody(
  form: ApplicationManagerProposalSectionProposalForm,
  app: Pick<ApplicationDetail, 'executor_company' | 'proposal'>,
): Record<string, unknown> {
  const companyName = app.executor_company?.name?.trim()
    || app.proposal?.company_name?.trim()
    || ''
  return {
    company_name: companyName || '—',
    services_description: '',
    currency_code: form.currency_code,
    terms: form.terms || '',
    service_lines: form.service_lines.map((line) => ({
      kind: line.kind,
      amount: parseProposalMoneyInput(line.amount),
      comment: line.comment.trim(),
    })),
  }
}
